// Version-awareness + sanity-check validation for .ALL/.PGM files — see
// FORMAT.md. The version string alone (e.g. "MPC1000 ALL 4.50") is a weak
// signal on its own: we only have real data confirming ONE value for each
// format (zero variation across all 201 real projects sampled), so we have
// no evidence about which *other* version strings would or wouldn't still
// share this exact byte layout — flagging purely on a string mismatch risks
// warning about a file that would have actually parsed just fine. The real
// gate is a sanity check on what actually got parsed: implausible values
// (a 0-bar sequence, a 4000bpm tempo, a PGM that isn't the fixed on-disk
// size) are a much more reliable "something is actually wrong" signal than
// the version string, which real hardware doesn't even reliably keep
// pristine on its own (a bit-flipped high bit on an isolated track-name
// character turns up in a handful of real, otherwise perfectly valid files
// in the reference library — see FORMAT.md).
import {
  ALL_PROFILE_SUPPORT_LABEL,
  PGM_PROFILE_SUPPORT_LABEL,
  detectAllProfile,
  detectPgmProfile,
} from './os-profile.js';

export const KNOWN_ALL_VERSION = 'MPC1000 ALL 4.50';
export const KNOWN_PGM_VERSION = 'MPC1000 PGM 8.00';
export const KNOWN_PGM_SIZE = 10756;

// Generous on both ends — real data in the reference library spans 1-261
// bars and 42-225 BPM; these bounds are meant to only catch genuinely
// implausible values (a misread field, a truly different byte layout), not
// to second-guess a real project we simply haven't seen yet.
const MIN_BARS = 1;
const MAX_BARS = 999;
const MIN_BPM = 20;
const MAX_BPM = 400;

/** @returns {{version: string, versionMatches: boolean, knownVersion: string, profile: import('./os-profile.js').Profile, looksOk: boolean, issues: string[]}} */
export function checkAllFormat(parsed, actualByteLength) {
  const issues = [];
  const profile = parsed.profile ?? detectAllProfile(parsed.version);
  const versionMatches = profile.supported;
  if (!versionMatches) {
    issues.push(`Unrecognized .ALL profile for version "${parsed.version}" (supported profiles: ${ALL_PROFILE_SUPPORT_LABEL}) - informational only, doesn't necessarily mean anything's wrong`);
  }
  if (parsed.fileSize !== actualByteLength) {
    issues.push(`Header-declared file size (${parsed.fileSize}) doesn't match the actual file (${actualByteLength} bytes)`);
  }
  for (const seq of parsed.sequences) {
    if (!(seq.bars >= MIN_BARS && seq.bars <= MAX_BARS)) {
      issues.push(`Sequence "${seq.name}" has an implausible bar count (${seq.bars})`);
    }
    if (!(seq.bpm >= MIN_BPM && seq.bpm <= MAX_BPM)) {
      issues.push(`Sequence "${seq.name}" has an implausible BPM (${seq.bpm})`);
    }
  }
  // A version mismatch alone never fails the check — only structural
  // implausibility does. This is the whole point: don't warn "unsupported"
  // about a file that would have loaded just fine.
  const structuralIssues = issues.filter((i) => !i.startsWith('Unrecognized'));
  return {
    version: parsed.version,
    versionMatches,
    knownVersion: profile.supported ? profile.knownVersion : ALL_PROFILE_SUPPORT_LABEL,
    profile,
    looksOk: structuralIssues.length === 0,
    issues,
  };
}

/** @returns {{version: string, versionMatches: boolean, knownVersion: string, profile: import('./os-profile.js').Profile, looksOk: boolean, issues: string[]}} */
export function checkPgmFormat(parsed, actualByteLength) {
  const issues = [];
  const profile = parsed.profile ?? detectPgmProfile(parsed.version);
  const versionMatches = profile.supported;
  if (!versionMatches) {
    issues.push(`Unrecognized .PGM profile for version "${parsed.version}" (supported profiles: ${PGM_PROFILE_SUPPORT_LABEL}) - informational only, doesn't necessarily mean anything's wrong`);
  }
  if (actualByteLength !== KNOWN_PGM_SIZE) {
    issues.push(`File size (${actualByteLength} bytes) doesn't match the expected fixed PGM size (${KNOWN_PGM_SIZE})`);
  }
  const structuralIssues = issues.filter((i) => !i.startsWith('Unrecognized'));
  return {
    version: parsed.version,
    versionMatches,
    knownVersion: profile.supported ? profile.knownVersion : PGM_PROFILE_SUPPORT_LABEL,
    profile,
    looksOk: structuralIssues.length === 0,
    issues,
  };
}
